import { OAuthClient } from '@whitehelmet/oauth';
import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';

const ACCESS_TOKEN_KEY = 'access_token';

@Injectable()
export class OAuthService {
  private onSignInResolver = (_isSignedIn: boolean) => {};
  public onSignIn = new Promise((resolve) => (this.onSignInResolver = resolve));
  public accessToken: string = '';

  private oauth = new OAuthClient(
    environment.oauth.origin,
    environment.oauth.realm,
    environment.oauth.client_id,
    environment.oauth.grant_type,
  );

  constructor() {
    this.accessToken = this.getAccessToken();
  }

  public setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    this.onSignInResolver(true);
  }

  getAccessToken() {
    const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (storedToken) {
      this.accessToken = storedToken;
    }
    return this.accessToken;
  }

  isSignedIn(): boolean {
    return this.getAccessToken() !== '';
  }

  async signIn(username: string, password: string) {
    const response = await this.oauth.requestAccessTokenWithUsername(username, password);
    if (response.success && response.otp === 'disabled') {
      this.setAccessToken(response.data.access_token);
    }
    return response;
  }

  async requestAccessTokenWithOtp(otp: string, username: string) {
    const response = await this.oauth.requestAccessTokenWithOtp(username, otp);
    if (response.success) {
      this.setAccessToken(response.access_token);
    }
    return response;
  }

  async resendOtp(username: string) {
    return this.oauth.resendOtp(username);
  }

  async signOut(token?: string) {
    let tokenToRevoke = token || this.accessToken || this.getAccessToken();

    if (!tokenToRevoke) {
      console.warn('[OAuthService.signOut] No token available to revoke');
      return { success: false, error: 'No token available' };
    }

    const response = await this.oauth.revokeAccessToken(tokenToRevoke);
    if (response.success) {
      this.setAccessToken('');
    }
    return response;
  }

  /**
   * Revoke the Keycloak token server-side without clearing local state.
   * Used during sign-out to revoke early while keeping localStorage intact
   * so the auth guard still allows navigation to the sign-out page.
   */
  async revokeToken(token?: string): Promise<any> {
    const tokenToRevoke = token || this.accessToken || this.getAccessToken();
    if (!tokenToRevoke) {
      console.warn('[OAuthService.revokeToken] No token available to revoke');
      return { success: false, error: 'No token available' };
    }
    return this.oauth.revokeAccessToken(tokenToRevoke);
  }

  async forgotPassword(username: string) {
    return this.oauth.forgotPassword(username);
  }

  async resetPassword(username: string, otp: string, newPassword: string) {
    return this.oauth.resetPassword(username, otp, newPassword);
  }

  async changeLoggedInUserPassword(
    email: string,
    current_password: string,
    new_password: string,
    access_token: string,
  ) {
    return this.oauth.changeLoggedInUserPassword(
      email,
      current_password,
      new_password,
      access_token,
    );
  }
}
